/**
 * Layers Management → Guide: how the layer stack works and how to use this
 * page, for the people who run it. Shown only once the page's own data has
 * loaded, i.e. after the server's platform.read check passed — the same
 * people the rest of the page is for.
 *
 * Every rule stated here is the code's, not a summary from memory; each
 * names where it lives so the next change to it can find this page too:
 *   chip colours        ops_qa_api._layer_status / _halftime_flag / _match_rows
 *   match state         layersLabModel.matchState / attentionReasons
 *   L1 append-only      app/layerstack/ingest (trdaas refuses, tracab and
 *                       Second Spectrum add zero rows on a re-run)
 *   overwriting         app/layerstack/storage.overwrite_allowed
 *   serving switch      ServeToggle.tsx (LAYERSTACK_READ=per_game)
 */
import type { ReactNode } from 'react'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="ls-guide__section">
      <h3>{title}</h3>
      {children}
    </section>
  )
}

const LAYERS: [string, string][] = [
  ['L1 — Raw delivery', 'The vendor’s files, kept exactly as delivered: one batch per file. Append-only — ingesting a file the stack already holds adds nothing.'],
  ['L2 — Vendor-shaped clean', 'The same data split into typed tables per vendor, converted to metres.'],
  ['L3 — Canonical space', 'One coordinate system for every vendor: 105 × 68 m, corner origin. Entities (players, teams, periods) are promoted here, so nothing above reads vendor tables.'],
  ['L4 — Events', 'On-ball events detected from L3 positions: passes, carries, shots and the rest.'],
  ['L5 — Structure', 'Team shape: formations, proximity, shape graphs, set-piece setups.'],
  ['L6 — Signals', 'The analytics: value models, spatial control, pressing, progressive runs. Model-versioned.'],
  ['Serve', 'What Match Video, the Insights tools and the API read — the only tables consumer surfaces should read.'],
]

export default function LayerStackGuide() {
  return (
    <div className="ls-guide">
      <p className="page-sub" style={{ marginTop: 0 }}>
        How a match&rsquo;s data is built, and how to use this page to bring a
        match onto the stack and keep it healthy. For what each table
        measures, see <a href="/ops/layer-stack?view=docs">Docs</a>.
      </p>

      <Section title="1. What the layer stack is">
        <p>
          Each match is built in layers, each one computed from the layer
          below it. A layer can be rebuilt without touching the ones under
          it, and everything above it follows.
        </p>
        <dl className="ls-guide__layers">
          {LAYERS.map(([name, what]) => (
            <div key={name}>
              <dt>{name}</dt>
              <dd>{what}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title="2. Reading the Matches list">
        <p>One row per match, with a chip for each layer and one for timings.</p>
        <table className="ls-guide__table">
          <tbody>
            <tr><th className="ls-guide__chip ls-guide__chip--green">Green</th>
              <td>Every required table is there and, where the layer records its runs, the latest run passed its checks.</td></tr>
            <tr><th className="ls-guide__chip ls-guide__chip--yellow">Yellow</th>
              <td>Partly built, or built but with failed checks or no check record. Hover the chip for which.</td></tr>
            <tr><th className="ls-guide__chip ls-guide__chip--red">Red</th>
              <td>Nothing of that layer exists yet.</td></tr>
          </tbody>
        </table>
        <p>
          <strong>Timings</strong> is green when the match has at least two
          periods with sensible frame ranges, yellow when it has fewer or
          a range looks wrong, red when it has none.
        </p>
        <p>Each match also gets a state:</p>
        <ul>
          <li><strong>Complete</strong> — every chip green, no failed build step, and served from the stack.</li>
          <li><strong>Attention</strong> — something is off. Hover it: it lists the failed steps, the chips that aren&rsquo;t green, timings, and whether it&rsquo;s served from the stack.</li>
          <li><strong>Convert</strong> — no tracking ingested to the stack yet.</li>
        </ul>
        <p>Click a row to open the match in <strong>Layer Stack</strong>.</p>
      </Section>

      <Section title="3. Bringing a match onto the stack">
        <ol>
          <li>
            <strong>Ingest tracking.</strong> On the match&rsquo;s Layer Stack
            view, pick the tracking file (Tracab also needs its metadata
            file) and ingest it. That fills L1.
          </li>
          <li>
            <strong>Create the stack record.</strong> Either
            <em> Build through Entities</em> (L2 → L3 → Entities, periods and
            teams from the vendor&rsquo;s own data — preferred), or
            <em> Import</em> from the legacy match record. Import needs the
            match timings and the home team&rsquo;s first-half attacking
            direction: set them with <em>Set match timings…</em>, which opens
            the video in its own window. Import refuses without the direction.
            The same button sits by Game Facts&rsquo; Timeline afterwards, to
            correct a time on the video.
          </li>
          <li>
            <strong>Check Game Facts.</strong> Match date, club side, teams,
            half times and pitch size. Changing home/away, the first-half
            direction or the pitch size queues a forced rebuild from L3, so
            everything above follows the new value.
          </li>
          <li>
            <strong>Build.</strong> <em>Build next</em> runs the next step
            that isn&rsquo;t built; <em>Build all</em> queues every remaining
            step in order. The Pipeline panel on the right shows each step and
            lets you build a single one.
          </li>
          <li>
            <strong>Turn serving on.</strong> Where serving is switched on per
            game, the switch on the match decides whether Match Video, the
            Insights tools and the API read it from the stack. Until then
            they show the legacy data.
          </li>
        </ol>
      </Section>

      <Section title="4. When something is wrong">
        <ul>
          <li>
            <strong>Start from the Attention tooltip</strong> on the Matches
            list: it names the failed step or the layer that isn&rsquo;t
            green. The match&rsquo;s own panel for that layer has the detail.
          </li>
          <li>
            <strong>Builds</strong> shows every build across all games: what
            is running, how far it got, what is queued behind it, and what
            finished. A batch can be cancelled there.
          </li>
          <li>
            <strong>Rebuilding over built layers.</strong> <em>Force
            (overwrite)</em> in the Pipeline panel, and <em>Rebuild L3+</em> on
            a match green through L2, replace what is built and queue
            everything above it. They appear only where this environment
            allows overwriting. L1 deliveries are never overwritten.
          </li>
          <li>
            <strong>A match that suddenly loads slowly</strong> after a
            release has usually been left on raw tables: its served copies
            were built under an older format. Rebuilding its Serve step brings
            it back.
          </li>
          <li>
            <strong>Corrections</strong>, on the match view, fix a tracking
            value over a frame range, with a reason that is kept.
          </li>
        </ul>
      </Section>

      <Section title="5. The other tabs">
        <ul>
          <li><strong>Match Lab</strong> — video, the spatial view and keyboard labeling for one match.</li>
          <li><strong>Builds</strong> — every build across all games (above).</li>
          <li><strong>Docs</strong> — what every table measures and how, and the data dictionary of the Serve tables.</li>
          <li><strong>Insights (X/Y)</strong>, on a match&rsquo;s card, opens it in Manage Insights; X of the Y registered insight packs have built for it.</li>
          <li><strong>Match Video</strong>, on a match&rsquo;s card, opens the club-facing match page in that match&rsquo;s club.</li>
        </ul>
      </Section>
    </div>
  )
}
